import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import { Box, Typography } from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'
import React, { CSSProperties, useEffect, useMemo, useState } from 'react'
import { useForm, useFieldArray, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import KumocanButton from 'components/button'
import KumocanDialog from 'components/dialog'
import KumocanSelectBox, { Option } from 'components/selectBox'
import { commonApi } from 'hooks/api/common'
import { companyBranchApi } from 'hooks/api/companyBranch'
import { shiftApi } from 'hooks/api/shift'
import { useSnackbar } from 'hooks/useSnackbar/SnackbarContext'
import { Holiday } from 'types/common'
export type SubmitShiftProps = {
  paidLeaveCount?: number
}

interface Shift {
  date: Dayjs
  schedule: number
}

interface Form {
  shifts: Shift[]
}

const SubmitShift: React.FC<SubmitShiftProps> = ({ paidLeaveCount }) => {
  const { t } = useTranslation(['page-shift', 'common'])
  const { showSnackbar } = useSnackbar()
  const [selectedMonth, setSelectedMonth] = useState<Dayjs | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)
  const [isCompleteDialogOpen, setIsCompleteDialogOpen] =
    useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [shiftChangeDeadlineDays, setShiftChangeDeadlineDays] = useState(7)

  const { control, handleSubmit, reset } = useForm<Form>({
    defaultValues: {
      shifts: [],
    },
  })
  const { fields } = useFieldArray({
    control,
    name: 'shifts',
  })
  const [allowedShiftOptions, setAllowedShiftOptions] = useState<Option[]>([])
  const [remainingPaidLeave, setRemainingPaidLeave] = useState<number>(
    paidLeaveCount ?? 0,
  )
  const [holidays, setHolidays] = useState<Holiday[]>([])
  const [selectedShifts, setSelectedShifts] = useState<number[]>([])
  const [initialSchedules, setInitialSchedules] = useState<Map<string, number>>(
    new Map(),
  )

  const watchedShifts: Shift[] = useWatch({ control, name: 'shifts' })

  // 祝日データとシフト変更期限日数、selectedShiftsを初期化時に取得
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // 祝日データを取得
        const holidaysRes = await commonApi().getHolidays()
        setHolidays(holidaysRes.data)

        // 支社情報を取得してselectedShiftsを設定
        const companyBranchRes = await companyBranchApi().getCompanyBranch()
        setSelectedShifts(companyBranchRes.data.companyBranch.selectedShifts)

        // シフト変更期限日数を取得（現在月の最初の1日分だけ取得）
        const today = dayjs()
        const startOfMonth = today.startOf('month')
        const endOfMonth = startOfMonth // 1日分だけ取得してAPIコールを最小化

        const shiftRes = await shiftApi().findByDate(startOfMonth, endOfMonth)
        if (shiftRes.data?.shiftChangeDeadlineDays !== undefined) {
          setShiftChangeDeadlineDays(shiftRes.data.shiftChangeDeadlineDays)
        }
      } catch (error) {
        console.error('初期データ取得エラー:', error)
        // エラーが発生してもデフォルト値（7）で継続
      }
    }

    fetchInitialData()
  }, [])

  // 曜日の取得（ロケール対応）
  const getWeekday = (date: Dayjs) => {
    return dayjs.weekdaysShort()[date.day()]
  }

  // 期限切れかどうかを判定
  const isPastDeadline = (date: Dayjs): boolean => {
    const today = dayjs().startOf('day')
    const targetDate = date.startOf('day')
    const daysDiff = targetDate.diff(today, 'day')
    return daysDiff < shiftChangeDeadlineDays
  }

  // 期限日テキストを取得
  const getDeadlineText = (date: Dayjs): string => {
    const deadline = date.subtract(shiftChangeDeadlineDays, 'day')
    return `${deadline.format(t('submit_shift.deadline_format'))}${t('submit_shift.deadline_suffix')}`
  }

  // 祝日かどうか判定
  const isHoliday = (date: Dayjs): boolean => {
    return holidays.some((holiday) => holiday.date.isSame(date, 'day'))
  }

  // カード全体の背景色を取得
  const getCardBackgroundColor = (date: Dayjs): string => {
    // 期限切れの場合はグレー
    if (isPastDeadline(date)) {
      return '#f5f5f5'
    }

    const dayOfWeek = date.day()
    if (dayOfWeek === 0 || isHoliday(date)) {
      return '#ffebee' // 日曜・祝日: 赤系
    } else if (dayOfWeek === 6) {
      return '#e3f2fd' // 土曜: 青系
    }
    return '#ffffff' // 平日: 白
  }

  // 提出画面に表示する月を取得
  const availableMonths = useMemo(() => {
    const base = dayjs().add(shiftChangeDeadlineDays, 'day')
    return [base, base.add(1, 'month'), base.add(2, 'month')]
  }, [shiftChangeDeadlineDays])

  // 月を選択したときの処理
  const handleMonthSelect = async (month: Dayjs) => {
    setSelectedMonth(month)

    // 選択された月の日付リストを生成
    const startOfMonth = month.startOf('month')
    const endOfMonth = month.endOf('month')
    const dates: Dayjs[] = []

    let currentDate = startOfMonth
    while (currentDate.isSame(endOfMonth, 'month')) {
      dates.push(currentDate)
      currentDate = currentDate.add(1, 'day')
    }

    // 既存のシフトデータを取得
    try {
      const response = await shiftApi().getMonthlyShifts(
        month.format('YYYY-MM'),
      )

      // shiftChangeDeadlineDaysを更新
      if (response.shiftChangeDeadlineDays !== undefined) {
        setShiftChangeDeadlineDays(response.shiftChangeDeadlineDays)
      }

      // フォームデータを初期化
      const shiftsData = dates.map((date) => {
        const existing = response.shifts?.find((s) =>
          dayjs(s.date).isSame(date, 'day'),
        )
        return {
          date,
          schedule: existing?.schedule || 0,
        }
      })

      // 初期値を保存（変更検知用）
      const scheduleMap = new Map<string, number>()
      shiftsData.forEach((s) => {
        scheduleMap.set(s.date.format('YYYY-MM-DD'), s.schedule)
      })
      setInitialSchedules(scheduleMap)

      reset({ shifts: shiftsData })
    } catch {
      // エラー時は空のシフトデータで初期化
      const shiftsData = dates.map((date) => ({ date, schedule: 0 }))
      setInitialSchedules(new Map())
      reset({ shifts: shiftsData })
    }
  }

  const handleOpen = () => {
    setIsDialogOpen(true)
  }

  const handleClose = () => {
    setIsDialogOpen(false)
  }

  const handleCompleteClose = () => {
    setIsCompleteDialogOpen(false)
  }

  const submit = async (data: Form) => {
    if (isSubmitting) return
    setIsSubmitting(true)

    try {
      // 初期値から変更があったシフトのみを送信
      const changedShifts = data.shifts.filter((shift) => {
        const dateKey = shift.date.format('YYYY-MM-DD')
        const initial = initialSchedules.get(dateKey) ?? 0
        return shift.schedule !== initial
      })

      if (changedShifts.length === 0) {
        setIsSubmitting(false)
        return
      }

      // submitエンドポイントを使用（ShiftManagementIDなし）
      await shiftApi().submit(changedShifts)

      setIsDialogOpen(false)
      setIsCompleteDialogOpen(true)
    } catch (error) {
      showSnackbar(
        t('submit_shift.submit_error', { message: (error as any).data.msg }),
        'error',
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const getAllowedShiftOptions = () => {
    // 全シフトオプション定義
    const allOptions: Option[] = [
      {
        value: SCHEDULE.DAY_OFF,
        label: t('submit_shift.schedule_options.day_off'),
      },
      { value: SCHEDULE.DAY, label: t('submit_shift.schedule_options.day') },
      {
        value: SCHEDULE.NIGHT,
        label: t('submit_shift.schedule_options.night'),
      },
      {
        value: SCHEDULE.CONTINUOUS,
        label: t('submit_shift.schedule_options.continuous'),
      },
      {
        value: SCHEDULE.DAY_OR_NIGHT,
        label: t('submit_shift.schedule_options.day_or_night'),
      },
      { value: SCHEDULE.AM, label: t('submit_shift.schedule_options.am') },
      { value: SCHEDULE.PM, label: t('submit_shift.schedule_options.pm') },
      {
        value: SCHEDULE.PAID_LEAVE_REQUEST,
        label: t('submit_shift.schedule_options.paid_leave_request'),
        disabled: remainingPaidLeave <= 0,
      },
    ]

    return allOptions.filter((o) => selectedShifts.includes(o.value as number))
  }

  useEffect(() => {
    setAllowedShiftOptions(getAllowedShiftOptions())
  }, [remainingPaidLeave, selectedShifts])

  useEffect(() => {
    // 有給使用数を計算
    const usedPaidLeave = watchedShifts.filter(
      (shift) =>
        shift.schedule === SCHEDULE.PAID_LEAVE ||
        shift.schedule === SCHEDULE.PAID_LEAVE_REQUEST,
    ).length

    setRemainingPaidLeave((paidLeaveCount ?? 0) - usedPaidLeave)
  }, [watchedShifts, paidLeaveCount])

  // レンダリング
  if (!selectedMonth) {
    // 月選択画面
    return (
      <Box sx={monthSelectionContainerStyle}>
        <Box sx={monthSelectionListStyle}>
          {availableMonths.map((month) => (
            <Box
              key={month.format('YYYY-MM')}
              onClick={() => handleMonthSelect(month)}
              sx={monthCardStyle}
            >
              <Typography variant="h6" sx={monthTextStyle}>
                {month.format(t('submit_shift.month_format'))}
              </Typography>
              <KeyboardArrowRightIcon sx={arrowIconStyle} />
            </Box>
          ))}
        </Box>
      </Box>
    )
  }

  return (
    <>
      <Box sx={mainContainerStyle}>
        {/* スティッキーフッターのスペースを確保 */}
        <Typography sx={noteTextStyle}>
          {t('submit_shift.note', { days: shiftChangeDeadlineDays })}
        </Typography>
        <Box sx={dateListContainerStyle}>
          {/* 日付一覧の上に16pxのスペースを追加 */}
          {fields.map((field, index) => {
            const date = field.date
            const schedule = watchedShifts[index]?.schedule || 0
            const weekday = getWeekday(date)
            const cardBackgroundColor = getCardBackgroundColor(date)
            const isDisabled = isPastDeadline(date)

            return (
              <Box
                key={field.id}
                sx={{
                  ...dateCardStyle,
                  backgroundColor: cardBackgroundColor,
                  opacity: isDisabled ? 0.6 : 1,
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      ...dateTextStyle,
                      color: isDisabled ? '#999' : '#333',
                    }}
                  >
                    {date.format(t('submit_shift.date_format'))}({weekday})
                  </Typography>
                  {isDisabled && (
                    <Typography sx={expiredTextStyle}>
                      {t('submit_shift.expired')}
                    </Typography>
                  )}
                </Box>

                <Box sx={selectBoxContainerStyle}>
                  <KumocanSelectBox
                    name={`shifts.${index}.schedule`}
                    control={control}
                    options={allowedShiftOptions}
                    style={selectStyle(schedule)}
                    disabled={isDisabled}
                  />
                  <PaidLeaveCount
                    schedule={schedule}
                    count={remainingPaidLeave}
                  />
                  {!isDisabled && (
                    <Typography sx={deadlineTextStyle}>
                      {getDeadlineText(date)}
                    </Typography>
                  )}
                </Box>
              </Box>
            )
          })}
        </Box>
      </Box>

      {/* スティッキーフッター */}
      <Box sx={stickyFooterStyle}>
        <KumocanButton
          onClick={() => setSelectedMonth(null)}
          variant="outlined"
          style={buttonStyle}
        >
          {t('common:buttons.back')}
        </KumocanButton>
        <KumocanButton
          onClick={handleOpen}
          variant="contained"
          style={buttonStyle}
        >
          {t('common:buttons.ok')}
        </KumocanButton>
      </Box>

      <KumocanDialog open={isDialogOpen} handleClose={handleClose}>
        <Typography variant="h6" sx={dialogTitleStyle}>
          {t('submit_shift.confirm_title')}
        </Typography>
        <Typography sx={dialogContentStyle}>
          {t('submit_shift.confirm_month', {
            month: selectedMonth?.format(
              t('submit_shift.confirm_month_format'),
            ),
          })}
        </Typography>
        <Box style={dialogActionStyle}>
          <KumocanButton
            variant="contained"
            color="secondary"
            style={dialogButtonStyle}
            onClick={handleClose}
          >
            {t('common:buttons.cancel')}
          </KumocanButton>
          <KumocanButton
            variant="contained"
            color="primary"
            style={dialogButtonStyle}
            onClick={handleSubmit(submit)}
            disabled={isSubmitting}
          >
            {t('submit_shift.submit_button')}
          </KumocanButton>
        </Box>
      </KumocanDialog>

      <KumocanDialog
        open={isCompleteDialogOpen}
        handleClose={handleCompleteClose}
      >
        <Typography variant="h6" sx={dialogTitleStyle}>
          {t('submit_shift.complete_title')}
        </Typography>
        <Typography sx={dialogContentStyle}>
          {t('submit_shift.complete_message')}
        </Typography>
        <Box sx={completedDialogButtonContainerStyle}>
          <KumocanButton
            variant="contained"
            color="primary"
            style={dialogButtonStyle}
            onClick={handleCompleteClose}
          >
            {t('common:buttons.close')}
          </KumocanButton>
        </Box>
      </KumocanDialog>
    </>
  )
}

// 有給選択時に表示する残数コンポーネント
const PaidLeaveCount = ({
  schedule,
  count,
}: {
  schedule: number
  count?: number
}) => {
  // 有給または有給申請が選択されている場合のみ表示
  const isPaidLeave =
    schedule === SCHEDULE.PAID_LEAVE || schedule === SCHEDULE.PAID_LEAVE_REQUEST

  if (!isPaidLeave || count === undefined) {
    return null
  }

  return <div style={paidLeaveCountStyle}>({count})</div>
}

// 有給残数表示用のスタイル
const paidLeaveCountStyle: CSSProperties = {
  position: 'absolute',
  left: '86px',
  top: '45%',
  transform: 'translateY(-45%)',
  fontSize: '15px',
  fontWeight: 'bold',
  color: 'white',
}

const shiftColor = (schedule: number): string => {
  switch (schedule) {
    case SCHEDULE.DAY_OFF:
      return 'rgb(175, 182, 182)'
    case SCHEDULE.DAY:
      return 'rgba(255, 72, 0, 0.5)'
    case SCHEDULE.NIGHT:
      return 'rgb(128, 176, 197)'
    case SCHEDULE.CONTINUOUS:
      return 'rgb(223, 173, 104)'
    case SCHEDULE.DAY_OR_NIGHT:
      return 'rgb(137, 211, 188)'
    case SCHEDULE.AM:
      return '#FFC5B9'
    case SCHEDULE.PM:
      return '#FFA896'
    case SCHEDULE.PAID_LEAVE:
      return '#4CAF50' // 有給: 緑系
    case SCHEDULE.PAID_LEAVE_REQUEST:
      return 'red'
    default:
      return 'white'
  }
}

const selectStyle = (schedule: number): CSSProperties => {
  const backColor = shiftColor(schedule)
  // 背景色によって文字色を調整
  const getTextColor = (schedule: number): string => {
    if (schedule === SCHEDULE.DAY_OFF || schedule === 0) {
      return '#333' // 休みや未選択の場合は黒
    }
    return 'white' // その他は白
  }

  return {
    width: '100%',
    height: '100%',
    display: 'inline-block',
    fontSize: '18px', // フォントサイズを少し小さく調整
    fontWeight: 'bold',
    lineHeight: '35px', // 高さに合わせてlineHeightも調整
    backgroundColor: backColor,
    color: getTextColor(schedule),
    border: 'none', // borderを削除
    borderRadius: '12px', // border-radiusを12pxに変更
    textAlign: 'left', // 左寄せに変更
    paddingLeft: '4px', // 左側にパディングを追加
  }
}

const dialogActionStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '10px',
  marginTop: '20px',
}

const dialogButtonStyle: CSSProperties = {
  width: '100%',
  fontSize: '18px',
  borderRadius: '4px',
  boxShadow: 'none',
  color: 'white',
  height: '50px',
}

const buttonStyle: CSSProperties = {
  flex: 1,
  height: '50px',
  fontSize: '18px',
  fontWeight: 'bold',
  borderRadius: '12px',
  boxShadow: 'none',
}

export const SCHEDULE = {
  DAY_OFF: 1,
  DAY: 2,
  NIGHT: 3,
  CONTINUOUS: 4,
  REVIEW: 5,
  DAY_OR_NIGHT: 6,
  TRAINING: 7,
  AM: 8,
  PM: 9,
  PAID_LEAVE: 100,
  PAID_LEAVE_REQUEST: 101,
}

// MUI Styles
const monthSelectionContainerStyle = {
  px: 2,
  mt: '16px',
}

const monthSelectionListStyle = {
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'wrap',
  gap: '16px',
}

const monthCardStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 20px',
  border: '1px solid #e0e0e0',
  borderRadius: '12px',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
  '&:active': {
    backgroundColor: '#e0e0e0',
  },
}

const monthTextStyle = {
  fontSize: '20px',
  fontWeight: 'bold',
  color: '#333',
}

const arrowIconStyle = {
  fontSize: '24px',
  color: '#666',
}

const mainContainerStyle = {
  px: 2,
  pb: '100px',
}

const noteTextStyle = {
  fontSize: '14px',
  color: '#666',
  mb: 2,
  textAlign: 'center',
  mt: '16px',
}

const dateListContainerStyle = {
  mb: 2,
}

const dateCardStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 20px',
  border: '1px solid #e0e0e0',
  borderRadius: '12px',
  mb: '16px',
  height: 'calc(80px - 32px)',
}

const dateTextStyle = {
  fontSize: '16px',
  fontWeight: 'bold',
}

const expiredTextStyle = {
  fontSize: '12px',
  color: '#999',
  mt: 0.5,
}

const selectBoxContainerStyle = {
  flex: 1,
  ml: 2,
  position: 'relative',
  height: 'calc(80px - 32px)',
}

const deadlineTextStyle = {
  position: 'absolute',
  right: '30px',
  top: '50%',
  transform: 'translateY(-50%)',
  fontSize: '14px',
  color: '#666',
  fontWeight: 'bold',
}

const stickyFooterStyle = {
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: 'white',
  borderTop: '1px solid #e0e0e0',
  padding: '16px',
  display: 'flex',
  gap: 2,
  zIndex: 1000,
}

const dialogTitleStyle = {
  textAlign: 'center',
  mb: 2,
}

const dialogContentStyle = {
  textAlign: 'center',
  my: 2,
}

const completedDialogButtonContainerStyle = {
  display: 'flex',
  justifyContent: 'center',
  mt: 2,
}

export default SubmitShift
